<template>
  <Drawer
    v-if="componentOptions"
    ref="drawerRef"
    v-model:visible="visible"
    :block-scroll="false"
    :header="componentOptions.header"
    :position="componentOptions.options?.position ? componentOptions.options?.position : 'right'"
    :pt-options="{
      fullWidth: componentOptions.typeId === 'helpers' ? true : false,
      hideHeader: componentOptions.options?.hideHeader,
      contentNoPadding: componentOptions.options?.contentNoPadding,
      viewType: componentOptions?.viewType
    }"
    :close-button-props="{
      rounded: false,
      severity: 'lite',
      ripple: true,
      class: 'flex'
    }"
    :dismissable="false"
    :class="{ 'isDarkMode': componentOptions.mode }"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <div class="w-[4rem] flex justify-start">
          <Button
            v-if="componentOptions.typeId === 'helpers'"
            severity="lite"
            @click="visible = false"
          >
            <i class="icon icon-left-arrow"></i>
          </Button>

          <Button
            v-if="componentOptions.previous"
            severity="lite"
            @click="componentOptions.previous?.submit()"
          >
            <i class="icon icon-left-arrow"></i>
          </Button>
        </div>

        <div
          v-if="componentOptions.header"
          class="font-serif flex items-center justify-center text-center w-full px-4"
        >
          {{ componentOptions.header }}
        </div>
      </div>
    </template>

    <template #default>
      <component
        :is="componentName"
        v-if="componentName"
        :id="props.id"
        :data="componentOptions.data"
        :options="drawerOptions[props.id].options"
        @return-data="(n) => {
          // Return Data
          if (typeof componentOptions?.returnData === 'function') {
            componentOptions.returnData(n)
          }

          // Submit
          if (typeof componentOptions?.buttons?.secondary?.submit === 'function' && componentOptions.autoClose !== false) {
            componentOptions.buttons.secondary.submit()
          }

          // Close
          if (componentOptions.autoClose !== false) {
            visible = false
          }

          // Redirect
          if (componentOptions.redirectLink) {
            navigateTo(componentOptions.redirectLink)
          }
        }"
        @is-pending="(n) => {
          isPending = n
        }"
      />

      <iframe 
        v-else-if="componentOptions.iframe"
        :src="componentOptions.iframe"
        class="w-full h-full"
        frameborder="0" 
      ></iframe>

      <div 
        v-else
        ref="formPanelRef"
        class="col"
      >
        <FormsGenerator
          :id="props.id"
          :fields="componentOptions.fields"
          :data="formData"
          @is-valid="(n) => {
            validForm = n
          }"
          @return-data="async (n) => {
            if (n) {
              formData = await n
                 
              // Close
              if (componentOptions.autocloseDrawerAfterExternalUpload && componentOptions.autoClose !== false) {
                visible = false
              }
            }

            // Redirect
            if (componentOptions.redirectLink) {
              navigateTo(componentOptions.redirectLink)
            }
          }"
          @submit-form="(n) => {
            if (n) {
              formData = n
            }

            formPanelRef.querySelector('.js-manuallyUpload').click()
          }"
          @is-pending="(n) => {
            isPending = n
          }"
        />
      </div>
    </template>
      
    <template
      v-if="componentOptions.buttons !== false && componentOptions.typeId !== 'helpers' 
        && ((componentOptions.buttons.secondary && componentOptions.buttons.secondary.hidden !== true) 
          || (componentOptions.buttons.primary && componentOptions.buttons.primary.hidden !== true)
        )"
      #footer
    >
      <div class="flex items-center gap-2">
        <Button
          v-if="componentOptions.buttons.secondary && componentOptions.buttons.secondary.hidden !== true"
          severity="lite"
          class="flex-auto"
          :disabled="componentOptions.inProcess || componentOptions.buttons.secondary.disabled"
          :label="componentOptions.buttons.secondary.label"
          :icon="componentOptions.buttons.secondary.icon"
          @click="() => {
            if (componentOptions.buttons.secondary.onClick) componentOptions.buttons.secondary.onClick();
            componentOptions.buttons.secondary.submit()
          }"
        >
        </Button>

        <Button
          v-if="componentOptions.buttons.primary && componentOptions.buttons.primary.hidden !== true"
          :label="componentOptions.buttons.primary.label"
          :icon="componentOptions.buttons.primary.icon"
          class="flex-auto"
          :disabled="isPending || componentOptions.buttons.primary.disabled"
          @click="() => {
            if (componentOptions.buttons.primary.onClick) componentOptions.buttons.primary.onClick();
            componentName ? componentOptions.buttons.primary.submit() : submitForm()
          }"
        >
        </Button>
      </div>
    </template>
  </Drawer>
</template>

<script setup>
/**
* Example:
  drawerOptions.value.address = {
    id: 'address',
    componentId: 'FormsGroupPayment',
    title: i18n?.t('addNewAddress'),
    buttons<optional>: {
      secondary: {
        label: 'Name',
        submit: () => {
        
        }
      },
      primary: {
        label: 'Name',
        submit: () => {
        
        }
      }  
    } 
  }
*/
const { $i18n: i18n } = useNuxtApp()
const { drawerOptions } = usePopups()

const drawerRef = ref(null)
const formPanelRef = ref(false)
const visible = ref(false)
const validForm = ref(true)
const isPending = ref(false)

/*
* Form Data
*/
const formData = ref({})

/**
* Props
*/
const props = defineProps({
  id: {
    type: [String],
    default: null
  }
})

/**
* Emits
*/
const emit = defineEmits(['close', 'returnData'])

/**
* Component Options
*/
const defaultOptions = {
  buttons: {
    secondary: false, 
    primary: {
      label: i18n?.t('submit'),
      submit: () => { 
        emit('returnData')
      }
    }
  },
  autoClose: true,
  inProcess: false,
  onClose: () => {
    visible.value = false
  }
}

drawerOptions.value[props.id] = deepMergeObjects(defaultOptions, drawerOptions.value[props.id])

const componentOptions = computed({
  get() {
    return drawerOptions.value[props.id]
  }
})

/**
* Component Name
*/
const componentName = computed({
  get() {
    if (componentOptions.value.componentId) {
      return defineComponent(componentOptions.value.componentId)
    } else { 
      return false
    }
  }
})

/*
* Form Data
*/
const autocompleteForm = () => {
  if (componentOptions.value?.fields && componentOptions.value.formDataCurrent) {
    for (const field of componentOptions.value.fields) {
      formData.value[field.name] = componentOptions.value.formDataCurrent[field.name]

      if (field.secondName) {
        formData.value[field.secondName] = componentOptions.value.formDataCurrent[field.secondName]
      }

      field.additionalFields?.forEach((item) => { 
        formData.value[item.name] = componentOptions.value.formDataCurrent[item.name]
      })
    }
  }
}

/**
* Submit Form
*/
const submitForm = () => {
  if (componentName.value) {
    // Close
    visible.value = false
  } else {
    const selector = formPanelRef.value.parentElement

    startValidateForm(selector)
  
    validateForm(selector, selector, validForm.value)
      .then(async (res) => {
        if (componentOptions.value.fieldType === 'greetingVideo') {
          formPanelRef.value.querySelector('.js-manuallyUpload').click()
        } else {
          // return and Close
          if (res) {
            componentOptions.value.returnData(formData.value)
            
            visible.value = false 
          }
        }
      })
  }
}

/**
* Fix Uncaught ResizeObserver loop completed with undelivered notifications.
*/
const resizeObserver = ref(null)
const disableResizeObserver = () => {
  if (window.ResizeObserver) {
    resizeObserver.value = new ResizeObserver(() => {})
    resizeObserver.value.disconnect()
  }
}

const enableResizeObserver = () => {
  if (resizeObserver.value) {
    resizeObserver.value = null
  }
}

/*
* Custom close Drawer after click outside
* Fix bud "Uncaught TypeError: Cannot read properties of null (reading 'children')" with "quill"
*/ 
const handleClickOutside = (event) => {
  if (drawerRef.value && !drawerRef.value.container?.contains(event.target) && event.target.dataset.pcSection === 'mask' && !event.target.dataset.imagePreview) {
    setTimeout(() => {
      visible.value = false
    }, 100)
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)

  enableResizeObserver()
})

/**
* Panel Visible
*/
watch([componentOptions, visible], ([newA, newB], [oldA, oldB]) => {
  if (newB !== oldB && newB === false) { 
    setTimeout(() => {
      emit('close')
    }, 500)
  }

  if (newA !== oldA) {
    visible.value = newA ? true : false
  }

  if (newB) {
    disableResizeObserver() 
  } else {
    enableResizeObserver()
  }
}, { deep: true })

/**
* Main Logic
*/
onMounted(() => { 
  visible.value = componentOptions.value ? true : false

  autocompleteForm()
})
</script>

<style lang="scss" scoped>

</style>
